import { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, differenceInCalendarWeeks } from 'date-fns';
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import api from '../services/api';

const COLORS = ['#4f46e5', '#16a34a', '#ea580c', '#2563eb', '#dc2626', '#0891b2', '#a21caf', '#ca8a04'];

const TYPE_LABELS = {
    standard: 'Standard',
    recup: 'Récupération',
    installation: 'Installation',
    envoi_tnt: 'Envoi TNT',
    envoi_postal: 'Envoi Postal',
    visite_patient: 'Visite patient',
};

const REFERENCE_MONDAY = new Date(2026, 5, 29);
const CYCLE = ['A', 'B', 'C', 'D'];

const getWeekType = (dateStr) => {
    const date = new Date(dateStr);
    const monday = startOfWeek(date, { weekStartsOn: 1 });
    const refMonday = startOfWeek(REFERENCE_MONDAY, { weekStartsOn: 1 });
    const diffWeeks = differenceInCalendarWeeks(monday, refMonday, { weekStartsOn: 1 });
    const index = ((diffWeeks % 4) + 4) % 4;
    return CYCLE[index];
};

const JOURS_FR = {
    'Monday': 'Lun', 'Tuesday': 'Mar', 'Wednesday': 'Mer',
    'Thursday': 'Jeu', 'Friday': 'Ven',
};
const JOURS_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

export default function StatsPage() {
    const [dateDebut, setDateDebut] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [dateFin, setDateFin] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
    const [stats, setStats] = useState(null);
    const [secteurStats, setSecteurStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateDebut, dateFin]);

    useEffect(() => {
        loadSecteurStats();
    }, []);

    const loadStats = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/stats?dateDebut=${dateDebut}&dateFin=${dateFin}`);
            setStats(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadSecteurStats = async () => {
        try {
            // Toujours sur toute l'année en cours
            const res = await api.get(`/stats?dateDebut=2026-01-01&dateFin=2026-12-31`);
            setSecteurStats(res.data.parSecteurJourSemaine || []);
        } catch (err) {
            console.error(err);
        }
    };

    const renderSecteurJourSemaine = () => {
        if (!secteurStats?.length) return null;

        const aggregated = {};
        secteurStats.forEach(row => {
            const weekType = getWeekType(row.date);
            const key = `${weekType}_${row.jour}_${row.secteur || 'Non défini'}`;
            if (!aggregated[key]) {
                aggregated[key] = { weekType, jour: row.jour, secteur: row.secteur || 'Non défini', couleur: row.couleur, total: 0 };
            }
            aggregated[key].total += parseInt(row.total);
        });

        const rows = Object.values(aggregated);

        return (
            <div style={{...cardStyle, marginTop: '20px'}}>
                <h3 style={cardTitleStyle}>
                    <i className="ti ti-map" style={{marginRight: '6px'}}/>
                    Répartition secteurs par jour et type de semaine
                    <span style={{fontSize: '11px', color: '#888', fontWeight: '400', marginLeft: '8px'}}>
                        (sur toute l'année, livraisons préparées)
                    </span>
                </h3>
                <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                    {['A', 'B', 'C', 'D'].map(semaine => {
                        const rowsSemaine = rows.filter(r => r.weekType === semaine);
                        if (rowsSemaine.length === 0) return null;

                        const parJour = {};
                        rowsSemaine.forEach(r => {
                            if (!parJour[r.jour]) parJour[r.jour] = [];
                            parJour[r.jour].push(r);
                        });

                        return (
                            <div key={semaine} style={{
                                backgroundColor: '#f8fafc',
                                borderRadius: '8px',
                                padding: '16px 20px',
                            }}>
                                <div style={{
                                    fontSize: '12px', fontWeight: '700', color: 'var(--accent-color)',
                                    backgroundColor: '#ede9fe', padding: '2px 8px', borderRadius: '4px',
                                    display: 'inline-block', marginBottom: '10px',
                                }}>
                                    Sem. {semaine}
                                </div>
                                <div style={{display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '6px'}}>
                                    {JOURS_ORDER.filter(j => parJour[j]).map(jour => {
                                        const joursRows = parJour[jour].sort((a, b) => b.total - a.total).slice(0, 5);
                                        const total = joursRows.reduce((s, r) => s + r.total, 0);
                                        return (
                                            <div key={jour}>
                                                <div style={{
                                                    fontSize: '10px',
                                                    fontWeight: '700',
                                                    color: '#888',
                                                    marginBottom: '4px',
                                                    textAlign: 'center'
                                                }}>
                                                    {JOURS_FR[jour]}
                                                </div>
                                                {joursRows.map(row => {
                                                    const pct = Math.round((row.total / total) * 100);
                                                    return (
                                                        <div key={row.secteur} style={{marginBottom: '3px'}}
                                                             title={`${row.secteur}: ${pct}%`}>
                                                            <div style={{
                                                                height: '6px',
                                                                borderRadius: '2px',
                                                                backgroundColor: '#e5e7eb'
                                                            }}>
                                                                <div style={{
                                                                    height: '100%', borderRadius: '2px',
                                                                    width: `${pct}%`,
                                                                    backgroundColor: row.couleur || '#4f46e5',
                                                                }}/>
                                                            </div>
                                                            <div style={{
                                                                fontSize: '11px',
                                                                color: row.couleur || '#888',
                                                                fontWeight: '600',
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }}>
                                                                {row.secteur} {pct}%
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div style={{padding: '24px'}}>
        {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: 'var(--text-main)' }}>
                    <i className="ti ti-chart-bar" style={{ marginRight: '8px' }} />Statistiques
                </h2>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} style={dateInputStyle} />
                    <span style={{ color: '#888' }}>→</span>
                    <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} style={dateInputStyle} />
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#888' }}>Chargement...</div>
            ) : !stats ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#888' }}>Aucune donnée</div>
            ) : (
                <>
                    {/* Ligne 1 : Total + Évolution */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: '20px', marginBottom: '20px' }}>
                        <div style={{
                            ...cardStyle,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                            <div style={{fontSize: '13px', color: '#888', marginBottom: '6px'}}>Total livraisons</div>
                            <div style={{
                                fontSize: '42px',
                                fontWeight: '700',
                                color: 'var(--accent-color)'
                            }}>{stats.total}</div>
                            <div style={{fontSize: '11px', color: '#aaa', marginTop: '4px'}}>sur la période</div>
                            <div style={{
                                marginTop: '12px',
                                borderTop: '1px solid #f0f0f0',
                                paddingTop: '12px',
                                textAlign: 'center'
                            }}>
                                <div style={{fontSize: '11px', color: '#888', marginBottom: '4px'}}>Moyenne / jour</div>
                                <div style={{fontSize: '22px', fontWeight: '700', color: 'var(--accent-color)'}}>
                                    {stats.parJour.length > 0 ? Math.round(stats.total / stats.parJour.length) : 0}
                                </div>
                            </div>
                        </div>
                        <div style={cardStyle}>
                            <h3 style={cardTitleStyle}>
                                <i className="ti ti-trending-up" style={{marginRight: '6px'}}/>Évolution du volume
                            </h3>
                            <ResponsiveContainer width="100%" height={200}>
                                <LineChart data={stats.parJour}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="date" tickFormatter={d => format(new Date(d), 'dd/MM')} fontSize={11} />
                                    <YAxis allowDecimals={false} fontSize={11} />
                                    <Tooltip labelFormatter={d => format(new Date(d), 'dd/MM/yyyy')} />
                                    <Line type="monotone" dataKey="total" stroke="#4f46e5" strokeWidth={2} dot={{ r: 2 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Ligne 2 : Par livreur / Par type / Par secteur */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                        <div style={cardStyle}>
                            <h3 style={cardTitleStyle}>
                                <i className="ti ti-truck" style={{ marginRight: '6px' }} />Par livreur
                            </h3>
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={stats.parLivreur} layout="vertical" margin={{ left: 10 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis type="number" allowDecimals={false} fontSize={11} />
                                    <YAxis type="category" dataKey="nom" width={80} fontSize={11} />
                                    <Tooltip />
                                    <Bar dataKey="total" fill="#4f46e5" radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div style={cardStyle}>
                            <h3 style={cardTitleStyle}>
                                <i className="ti ti-package" style={{ marginRight: '6px' }} />Par type de livraison
                            </h3>
                            <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                    <Pie
                                        data={stats.parType.map(t => ({ ...t, label: TYPE_LABELS[t.typelivraison] || t.typelivraison }))}
                                        dataKey="total" nameKey="label"
                                        cx="50%" cy="50%" outerRadius={75}
                                        label={({ label, total }) => `${label}: ${total}`}
                                        labelLine={false}
                                    >
                                        {stats.parType.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        <div style={cardStyle}>
                            <h3 style={cardTitleStyle}>
                                <i className="ti ti-map" style={{ marginRight: '6px' }} />Par secteur
                            </h3>
                            <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                    <Pie
                                        data={stats.parSecteur.map(s => ({ ...s, label: s.nom || 'Non défini' }))}
                                        dataKey="total" nameKey="label"
                                        cx="50%" cy="50%" outerRadius={75}
                                        label={({ label, total }) => `${label}: ${total}`}
                                        labelLine={false}
                                    >
                                        {stats.parSecteur.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Répartition secteurs par jour/semaine — indépendant des dates */}
                    {renderSecteurJourSemaine()}
                </>
            )}
        </div>
    );
}

const cardStyle = {
    backgroundColor: 'var(--card-bg)',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
};
const cardTitleStyle = { margin: '0 0 12px', fontSize: '14px', fontWeight: '700', color: 'var(--text-main)' };
const dateInputStyle = { padding: '8px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' };